import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { Button, Input, Textarea, Checkbox, Skeleton, Card, CardHeader, CardTitle, CardDescription, CardBody } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_ANTI_LEECH,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './AntiLeechForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = ADVANCED_TYPE_ANTI_LEECH;

/** The vendor's default Static Resources extension list for a new Anti-Leech rule. */
const DEFAULT_RES_SUFFIX =
  'jpg,gif,bmp,jpeg,png,tiff,rm,rmvb,wmv,avi,mkv,mp3,mp4,wma,zip,rar,svg,flv,mpg,mov,3gp,dat,vob,mkv,mpeg,7z,gz';

/** The Anti-Leech (type 1) per-type config. */
interface AntiLeechConfig {
  res_suffix: string;
  site_whitelist: string;
  url_whitelist: string;
}

interface FormState extends AntiLeechConfig {
  name: string;
  description: string;
  sites: number[];
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  res_suffix: DEFAULT_RES_SUFFIX,
  site_whitelist: '',
  url_whitelist: '',
  sites: [],
});

/**
 * Add / Edit an Anti-Leech rule (AI-WAF → Advanced Protection, waf_advanced_strategy
 * type 1). Account-level: applied to one or more sites via "Apply to". Builds the
 * type-specific `config` ({res_suffix, site_whitelist, url_whitelist}); the BE adds
 * the common envelope keys. Routes: aiwaf/advanced/anti-leech/new and .../:id.
 */
export function AntiLeechForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<AntiLeechConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as AntiLeechConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      res_suffix: c.res_suffix ?? DEFAULT_RES_SUFFIX,
      site_whitelist: c.site_whitelist ?? '',
      url_whitelist: c.url_whitelist ?? '',
      sites: existing.sites ?? [],
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<AntiLeechConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'anti_leech_rule', 'create');
      show({ tone: 'success', title: 'Anti-Leech rule created' });
      navigate(`${base}/aiwaf/advanced?tab=anti-leech`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<AntiLeechConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'anti_leech_rule', 'edit');
      show({ tone: 'success', title: 'Anti-Leech rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=anti-leech`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const resValid = form.res_suffix.trim().length > 0;
  const canSubmit = nameValid && resValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<AntiLeechConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        res_suffix: form.res_suffix.trim(),
        site_whitelist: form.site_whitelist.trim(),
        url_whitelist: form.url_whitelist.trim(),
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit Anti-Leech Rule' : 'Anti-Leech Rule' },
            ]}
          />
        }
        title="Anti-Leech Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
              <CardDescription>Define which static resources are protected and which sites/paths may bypass the check.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Static Resources"
                required
                description="File extensions of static resources untrusted third-party sites cannot access. Case-insensitive, comma-separated."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    value={form.res_suffix}
                    placeholder="jpg,gif,png,mp4,zip"
                    onChange={(e) => set('res_suffix', e.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Trusted Sites"
                description="Sites allowed to access the resources above. Supports domains, IPv4/IPv6, and * wildcards (e.g. *.abc.com). Comma-separated. Leave empty to block all other sites."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    value={form.site_whitelist}
                    placeholder="*.abc.com, www.example.com"
                    onChange={(e) => set('site_whitelist', e.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Whitelist Path"
                description="Paths whose requests can access static resources without a referer. Case-sensitive, supports * (e.g. /static/*). Comma-separated. Leave empty to block referer-less requests under these paths."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    value={form.url_whitelist}
                    placeholder="/static/*"
                    onChange={(e) => set('url_whitelist', e.target.value)}
                  />
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=anti-leech`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}
